/**
 * Step 3: the landing page for the link in the confirmation email.
 *
 * The verification itself has already happened by the time this renders —
 * the link points at Better Auth's own endpoint, which verifies the token and
 * then redirects here, with `?error=` when it would not accept it. So this
 * page reports an outcome; it does not perform one.
 */

import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { safeReturnPath, signInAfterRegistration } from '../../shared/return-path.ts'
import { resendVerificationInputSchema } from '../../shared/auth-schemas.ts'
import type { MessageKey } from '../../shared/message-keys.ts'
import { Alert } from '../components/Alert.tsx'
import { Field } from '../components/Field.tsx'
import { useI18n } from '../i18n/index.tsx'
import { authClient } from '../lib/auth-client.ts'

export function VerifyEmail() {
  const { t } = useI18n()
  const [params] = useSearchParams()
  const failed = params.has('error')
  // Carried from registration in the link's callbackURL (#105).
  const returnTo = safeReturnPath(params.get('next'))

  const [email, setEmail] = useState('')
  const [resent, setResent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<MessageKey | null>(null)

  async function resend() {
    setError(null)
    // Parsed at the edge, as every other form in the loop is: a mistyped
    // address is told so here, in the reader's language, with no request.
    const parsed = resendVerificationInputSchema.safeParse({ email })
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.invalid_email') as MessageKey)
      return
    }
    setBusy(true)
    // The response is the same whether or not the address exists.
    await authClient.sendVerificationEmail({
      email: parsed.data.email,
      callbackURL: `${window.location.origin}/verify${returnTo === null ? '' : `?next=${encodeURIComponent(returnTo)}`}`,
    })
    setBusy(false)
    setResent(true)
  }

  if (!failed) {
    return (
      <section className="panel">
        <h1>{t('verify.success.title')}</h1>
        <p>{t('verify.success.body')}</p>
        <Link to={signInAfterRegistration(returnTo)} className="button">
          {t('signin.submit')}
        </Link>
      </section>
    )
  }

  return (
    <section className="panel">
      <h1>{t('verify.failed.title')}</h1>
      <Alert tone="error">{t('verify.failed.body')}</Alert>

      {resent ? (
        <Alert tone="info">{t('verify.resent')}</Alert>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault()
            void resend()
          }}
          noValidate
        >
          <Field
            label={t('register.email.label')}
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
            }}
          />
          {error ? <Alert tone="error">{t(error)}</Alert> : null}
          <button type="submit" className="button" disabled={busy}>
            {t('verify.resend')}
          </button>
        </form>
      )}
    </section>
  )
}
